import { ticketingFetch } from "./client";

export type CurrentUser = {
  id: number;
  full_name: string;
  email: string;
  role: "Requester" | "Staff" | "Admin";
  company_id: number;
  department_id: number | null;
  avatar_url: string | null;
  must_change_password: boolean;
};

type AuthResponse = {
  user: CurrentUser;
};

type FastApiValidationError = {
  type?: string;
  loc?: unknown[];
  msg?: string;
  ctx?: Record<string, unknown>;
};

type ErrorResponse = {
  detail?: string | FastApiValidationError[];
};

export class PasswordChangeApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PasswordChangeApiError";
  }
}

const NEW_PASSWORD_MIN_LENGTH = 8;

function isFastApiValidationErrorArray(value: unknown): value is FastApiValidationError[] {
  if (!Array.isArray(value)) {
    return false;
  }
  return value.every((entry) => entry !== null && typeof entry === "object");
}

function messageForNewPasswordValidationErrors(errors: FastApiValidationError[]): string | null {
  for (const entry of errors) {
    const loc = Array.isArray(entry.loc) ? entry.loc : [];
    const field = loc[loc.length - 1];
    if (field !== "new_password") {
      continue;
    }
    const ctx = (entry.ctx ?? {}) as Record<string, unknown>;
    const ctxMinLength = typeof ctx.min_length === "number" ? ctx.min_length : null;
    const errorType = typeof entry.type === "string" ? entry.type : "";
    if (errorType === "string_too_short" || ctxMinLength !== null) {
      const limit = ctxMinLength ?? NEW_PASSWORD_MIN_LENGTH;
      return `New password must be at least ${limit} characters.`;
    }
    if (typeof entry.msg === "string" && entry.msg.length > 0) {
      return entry.msg;
    }
  }
  return null;
}

export async function login(email: string, password: string): Promise<CurrentUser> {
  const response = await ticketingFetch("/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    throw new Error("Login failed");
  }

  const payload = (await response.json()) as AuthResponse;
  return payload.user;
}

export async function logout(): Promise<void> {
  await ticketingFetch("/auth/logout", {
    method: "POST",
  });
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const response = await ticketingFetch("/auth/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });

  if (!response.ok) {
    let message = "Unable to change password. Please try again.";
    try {
      const payload = (await response.json()) as ErrorResponse;
      if (typeof payload.detail === "string" && payload.detail.length > 0) {
        message = payload.detail;
      } else if (isFastApiValidationErrorArray(payload.detail)) {
        const validationMessage = messageForNewPasswordValidationErrors(payload.detail);
        if (validationMessage !== null) {
          message = validationMessage;
        }
      }
    } catch {
      // Use the safe fallback message for malformed error responses.
    }
    throw new PasswordChangeApiError(message);
  }
}

export async function getCurrentUser(): Promise<CurrentUser> {
  const response = await ticketingFetch("/auth/me", {
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error("Not authenticated");
  }

  const payload = (await response.json()) as AuthResponse;
  return payload.user;
}
